/*
  Adds checked upsell products to the cart before replaying the main product form submit.
  Used by snippets/product-upsells.liquid.
*/

class ProductUpsells extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.errorElement = this.querySelector('[data-upsell-error]');
    this.modal = this.querySelector('[data-upsell-modal]');
    this.modalProduct = this.querySelector('[data-upsell-modal-product]');
    this.modalSelect = this.querySelector('[data-upsell-modal-select]');
    this.form = this.findProductForm();
    this.replayingSubmit = false;
    this.activeVariantCard = null;
    this.previousFocus = null;
    this.handleKeydown = this.handleKeydown.bind(this);

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-upsell-checkbox]')) {
        this.handleCheckboxChange(event.target);
      }

      if (event.target.matches('[data-upsell-variant-select]')) {
        this.updateVariant(event.target);
      }
    });

    if (this.modal) {
      this.modal.addEventListener('click', (event) => {
        if (event.target.closest('[data-upsell-modal-confirm]')) {
          this.confirmVariantModal();
          return;
        }

        if (event.target.closest('[data-upsell-modal-close], [data-upsell-modal-cancel]')) {
          this.closeVariantModal();
        }
      });

      document.addEventListener('keydown', this.handleKeydown);
    }

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this), true);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  findProductForm() {
    const formId = this.getAttribute('data-product-form-id');

    if (formId) {
      const formById = document.getElementById(formId);
      if (formById) return formById;
    }

    const nearbyForm = this.closest('form[action*="/cart/add"]');
    if (nearbyForm) return nearbyForm;

    const section = this.closest('[id^="shopify-section-"], .shopify-section, section');
    if (section) {
      const formInSection = section.querySelector('form[action*="/cart/add"]');
      if (formInSection) return formInSection;
    }

    return document.querySelector('form[action*="/cart/add"]');
  }

  handleCheckboxChange(checkbox) {
    if (!checkbox.checked) return;

    const card = checkbox.closest('[data-upsell-card]');
    const select = card?.querySelector('[data-upsell-variant-select]');

    if (select) {
      this.openVariantModal(card);
    }
  }

  updateVariant(select) {
    const card = select.closest('.product-upsells__card');
    if (!card) return;

    const checkbox = card.querySelector('[data-upsell-checkbox]');
    const selectedOption = select.options[select.selectedIndex];
    const price = card.querySelector('[data-upsell-price]');

    if (checkbox && selectedOption) {
      checkbox.dataset.variantId = selectedOption.value;
      checkbox.disabled = selectedOption.disabled;
    }

    if (price && selectedOption?.dataset.price) {
      price.textContent = selectedOption.dataset.price;
    }
  }

  openVariantModal(card) {
    const sourceSelect = card.querySelector('[data-upsell-variant-select]');
    if (!this.modal || !this.modalSelect || !sourceSelect) return;

    this.activeVariantCard = card;
    this.previousFocus = document.activeElement;
    this.modalSelect.innerHTML = '';

    Array.from(sourceSelect.options).forEach((option) => {
      this.modalSelect.add(option.cloneNode(true));
    });

    this.modalSelect.value = sourceSelect.value;

    if (this.modalProduct) {
      this.modalProduct.textContent = card.dataset.upsellTitle || '';
    }

    this.modal.hidden = false;
    document.documentElement.classList.add('product-upsells-modal-open');

    window.requestAnimationFrame(() => {
      this.modalSelect.focus();
    });
  }

  confirmVariantModal() {
    const sourceSelect = this.activeVariantCard?.querySelector('[data-upsell-variant-select]');
    if (sourceSelect && this.modalSelect) {
      sourceSelect.value = this.modalSelect.value;
      this.updateVariant(sourceSelect);
    }

    this.closeVariantModal({ keepChecked: true });
  }

  closeVariantModal(options = {}) {
    const { keepChecked = false } = options;
    const checkbox = this.activeVariantCard?.querySelector('[data-upsell-checkbox]');

    if (!keepChecked && checkbox) {
      checkbox.checked = false;
    }

    if (this.modal) {
      this.modal.hidden = true;
    }

    document.documentElement.classList.remove('product-upsells-modal-open');

    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }

    this.activeVariantCard = null;
    this.previousFocus = null;
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.modal && !this.modal.hidden) {
      this.closeVariantModal();
    }
  }

  selectedItems() {
    return Array.from(this.querySelectorAll('[data-upsell-checkbox]:checked:not(:disabled)'))
      .map((checkbox) => ({
        id: Number(checkbox.dataset.variantId),
        quantity: Number(checkbox.dataset.quantity || 1),
      }))
      .filter((item) => item.id && item.quantity > 0);
  }

  async handleSubmit(event) {
    if (this.replayingSubmit) {
      this.replayingSubmit = false;
      return;
    }

    const items = this.selectedItems();
    if (!items.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.setError('');

    const submitter = event.submitter || this.form.querySelector('[type="submit"]');
    this.setBusy(submitter, true);

    try {
      await this.addUpsells(items);
      this.setBusy(submitter, false);
      this.replayingSubmit = true;
      this.submitOriginalForm(submitter);
    } catch (error) {
      this.setBusy(submitter, false);
      this.setError(error.message || 'Unable to add selected extras. Please try again.');
    }
  }

  async addUpsells(items) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    });

    if (response.ok) return response.json();

    let message = 'Unable to add selected extras. Please try again.';

    try {
      const error = await response.json();
      message = error.description || error.message || message;
    } catch (_error) {
      // Keep the generic message if Shopify returns a non-JSON error.
    }

    throw new Error(message);
  }

  submitOriginalForm(submitter) {
    if (submitter && typeof this.form.requestSubmit === 'function') {
      this.form.requestSubmit(submitter);
      return;
    }

    if (typeof this.form.requestSubmit === 'function') {
      this.form.requestSubmit();
      return;
    }

    this.form.submit();
  }

  setBusy(submitter, isBusy) {
    this.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    if (submitter) {
      submitter.disabled = isBusy;
    }
  }

  setError(message) {
    if (!this.errorElement) return;

    this.errorElement.textContent = message;
    this.errorElement.hidden = !message;
  }
}

if (!customElements.get('product-upsells')) {
  customElements.define('product-upsells', ProductUpsells);
}